import moment, { Moment, MomentInput } from 'moment-timezone';
import { isNumeric } from 'utils/object';

export const UTC_TIME_ZONE = 'UTC';
export const EST_TIME_ZONE = 'America/New_York';
export const USER_TIME_ZONE_LOCAL_STORAGE_KEY = 'userTimeZone';

export const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';
export const FULL_DATE_FORMAT = 'MMM DD, YYYY';
export const FULL_DATE_TIME_FORMAT = 'MMM DD, YYYY HH:mm';
export const FULL_DATE_TIME_TZ_FORMAT = 'MMM DD, YYYY HH:mm zz';

export function parseDate(str?: string, format = DEFAULT_DATE_FORMAT, timeZone = UTC_TIME_ZONE) {
  try {
    if (!str) {
      return undefined;
    }

    const parsed = moment(str, format, timeZone);

    // if date is valid, return it
    if (moment(parsed).isValid()) {
      return parsed.toDate();
    }

    // date is not valid, return undefined
    return undefined;
  } catch (e) {
    console.warn('Failed to parse date', str, e);
    return undefined;
  }
}

export function formatDate(date?: Date, format?: string, timeZone = UTC_TIME_ZONE) {
  try {
    const validDate = date && moment(date);
    if (!validDate || !validDate.isValid()) {
      return undefined;
    }

    return validDate.tz(timeZone || moment.tz.guess()).format(format || DEFAULT_DATE_FORMAT);
  } catch (e) {
    console.warn('Failed to format date in timezone', date, timeZone, format, e);
    return undefined;
  }
}

export function formatDateRelative(date?: Moment | Date | number, withoutSuffix?: boolean) {
  try {
    return date && moment(date).fromNow(withoutSuffix);
  } catch (e) {
    console.warn('Failed to format date', date, e);
    return undefined;
  }
}

export function formatHoldPeriod(date?: Moment | Date | number) {
  if (!date) {
    return;
  }

  if (!(date instanceof Date || moment.isMoment(date) || isNumeric(date))) {
    return;
  }

  const monthsDiff = moment(date).diff(moment(), 'months', true);
  const yearsDiff = Math.max(Math.ceil(monthsDiff / 12), 1);
  return yearsDiff > 1 ? `${yearsDiff} Years` : '1 Year';
}

export function getTimestamp(date?: Moment | Date | number | unknown) {
  if (!date) {
    return;
  }

  if (!(date instanceof Date || moment.isMoment(date) || isNumeric(date))) {
    return;
  }

  const parsed = moment(date as MomentInput);
  if (!parsed.isValid()) {
    return;
  }

  return parsed.toDate().getTime();
}

export function getDate(timestamp?: number) {
  if (timestamp) return new Date(timestamp);
}

export function compareQuarters(q1: string, q2: string) {
  const a_parts = q1.split(' ');
  const b_parts = q2.split(' ');
  if (a_parts.length != 2) return -1;
  if (b_parts.length != 2) return 1;
  return a_parts[1] < b_parts[1]
    ? -1
    : a_parts[1] > b_parts[1]
    ? 1
    : a_parts[0] < b_parts[0]
    ? -1
    : a_parts[0] > b_parts[0]
    ? 1
    : 0;
}

export const getUserTimeZone = () => moment.tz.guess();

export const getTimeZoneCode = (tz: string) => moment().tz(tz).format('z');

export const getStoredTimeZone = () => {
  const storedValue = localStorage.getItem(USER_TIME_ZONE_LOCAL_STORAGE_KEY);
  if (storedValue) {
    return storedValue;
  }
};
